import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Card, Typography, Button, Descriptions, Row, Col, Space, Tabs, message, List, Modal, Form, Input } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCustomerById, updateCustomer, deleteCustomer } from '../../api/customers';
import CustomerModal from '../../components/sales/CustomerModal';
import ContactEditModal from '../../components/modal/ContactEditModal';
import AddressEditModal from '../../components/modal/AddressEditModal';
import ConfirmDeleteModal from '../../components/modal/ConfirmDeleteModal';
import type { Contact } from '../../types/common';
import type { Address } from '../../types/customers';
import type { Customer } from '../../types/customers';
import { getWorkspaceById, addWorkspace, updateWorkspace } from '../../api/workspaces';
import type { Workspace } from '../../types/workspaces';
// TODO: import reusable components for price plan, workspace, etc.

const { Title } = Typography;

// Utility function to convert URLs in text to clickable links
const formatTextWithLineBreaks = (text: string): React.ReactNode => {
  if (!text) return text;

  // URL regex pattern that matches http/https URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  const linkifyText = (text: string): React.ReactNode => {
    const parts = text.split(urlRegex);
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1890ff', textDecoration: 'underline' }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return text.split('\n').map((line, index, array) => (
    <React.Fragment key={index}>
      {linkifyText(line)}
      {index < array.length - 1 && <br />}
    </React.Fragment>
  ));
};

const CustomerDetailsPage: React.FC = () => {
  const { t } = useTranslation(['sales', 'common', 'workspaces']);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('details');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [openContactModal, setOpenContactModal] = useState(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [customer, setCustomer] = useState(location.state?.customer || null);
  const queryClient = useQueryClient();
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [openAddressModal, setOpenAddressModal] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<Address | null>(null);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [openDeleteAddressModal, setOpenDeleteAddressModal] = useState(false);
  const [openDeleteContactModal, setOpenDeleteContactModal] = useState(false);
  const navigate = useNavigate();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [workspaceEditModalOpen, setWorkspaceEditModalOpen] = useState(false);
  const [workspaceForm, setWorkspaceForm] = useState<any>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);

  // Only fetch if not passed in state
  const { data: fetchedCustomer } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => getCustomerById(id!),
    enabled: !customer && !!id,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (fetchedCustomer) setCustomer(fetchedCustomer);
  }, [fetchedCustomer]);

  useEffect(() => {
    if (customer) {
      setAddresses(customer.addresses ? [...customer.addresses] : []);
      setContacts(customer.contacts || []);
    }
  }, [customer]);

  useEffect(() => {
    if (customer && customer.workspace_id) {
      getWorkspaceById(customer.workspace_id).then(setWorkspace);
    } else {
      setWorkspace(null);
    }
  }, [customer]);

  const handleEdit = () => {
    setEditingCustomer(customer as Customer);
    setEditModalOpen(true);
  };

  const handleEditModalSuccess = () => {
    setEditModalOpen(false);
    setEditingCustomer(null);
    queryClient.invalidateQueries({ queryKey: ['customer', id] });
  };

  // Contacts add/edit/delete handlers
  const handleAddContact = () => {
    setEditingContact(null);
    setOpenContactModal(true);
  };
  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setOpenContactModal(true);
  };
  const handleSaveContact = async (newContacts: Contact[]) => {
    try {
      await updateCustomer(customer.id, { contacts: newContacts });
      setContacts(newContacts);
      setOpenContactModal(false);
      setEditingContact(null);
      message.success(t('sales:success.updateSuccess', { entity: t('common:contacts') }));
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
    } catch (e) {
      message.error(t('sales:errors.updateFailed', { entity: t('common:contacts') }));
    }
  };
  const handleDeleteContact = (contact: Contact) => {
    setContactToDelete(contact);
    setOpenDeleteContactModal(true);
  };
  const confirmDeleteContact = async () => {
    if (!contactToDelete) return;
    const newContacts = contacts.filter(c => c !== contactToDelete);
    try {
      await updateCustomer(customer.id, { contacts: newContacts });
      setContacts(newContacts);
      message.success(t('sales:success.updateSuccess', { entity: t('common:contacts') }));
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
    } catch (e) {
      message.error(t('sales:errors.updateFailed', { entity: t('common:contacts') }));
    }
    setOpenDeleteContactModal(false);
    setContactToDelete(null);
  };

  // Address CRUD handlers
  const handleAddAddress = () => {
    setEditingAddress(null);
    setOpenAddressModal(true);
  };
  const handleEditAddress = (address: Address) => {
    setEditingAddress(address);
    setOpenAddressModal(true);
  };
  const handleSaveAddress = async (address: Address) => {
    let newAddresses;
    if (editingAddress) {
      newAddresses = addresses.map(a => a === editingAddress ? address : a);
    } else {
      newAddresses = [...addresses, address];
    }
    try {
      await updateCustomer(customer.id, { addresses: newAddresses });
      setAddresses(newAddresses);
      setOpenAddressModal(false);
      setEditingAddress(null);
      message.success(t('sales:success.updateSuccess', { entity: t('common:addresses') }));
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
    } catch (e) {
      message.error(t('sales:errors.updateFailed', { entity: t('common:addresses') }));
    }
  };
  const handleDeleteAddress = (address: Address) => {
    setAddressToDelete(address);
    setOpenDeleteAddressModal(true);
  };
  const confirmDeleteAddress = async () => {
    if (!addressToDelete) return;
    const newAddresses = addresses.filter(a => a !== addressToDelete);
    try {
      await updateCustomer(customer.id, { addresses: newAddresses });
      setAddresses(newAddresses);
      message.success(t('sales:success.updateSuccess', { entity: t('common:addresses') }));
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
    } catch (e) {
      message.error(t('sales:errors.updateFailed', { entity: t('common:addresses') }));
    }
    setOpenDeleteAddressModal(false);
    setAddressToDelete(null);
  };

  // Workspace edit handlers
  const handleEditWorkspace = () => {
    setWorkspaceForm(workspace ? { ...workspace } : {
      name: customer.property_name,
      slug: customer.property_name?.toLowerCase().replace(/\s+/g, '-') || '',
      customer_id: customer.id,
      status: 'active',
      language: 'en',
      currency: 'USD',
    });
    setWorkspaceEditModalOpen(true);
  };
  const handleWorkspaceFormChange = (field: string, value: any) => {
    setWorkspaceForm((prev: any) => ({ ...prev, [field]: value }));
  };
  const handleSaveWorkspace = async () => {
    try {
      let workspaceId = workspace?.id;
      if (workspace) {
        await updateWorkspace(workspace.id, workspaceForm);
        message.success(t('sales:success.updateSuccess', { entity: t('sales:customers.workspace', 'Workspace') }));
      } else {
        // Create new workspace
        const newWorkspace = { ...workspaceForm, customer_id: customer.id };
        workspaceId = await addWorkspace(newWorkspace);
        message.success(t('sales:success.createSuccess', { entity: t('sales:customers.workspace', 'Workspace') }));
      }
      // Update customer with workspace linkage fields
      await updateCustomer(customer.id, {
        workspace_id: workspaceId,
        workspace_name: workspaceForm.name,
        workspace_slug: workspaceForm.slug,
      });
      setCustomer((prev: any) => ({ ...prev, workspace_id: workspaceId, workspace_name: workspaceForm.name, workspace_slug: workspaceForm.slug }));
      setWorkspaceEditModalOpen(false);
      // Refetch workspace info
      if (workspaceId) {
        getWorkspaceById(workspaceId).then(setWorkspace);
      }
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
    } catch (e) {
      message.error(t('sales:errors.updateFailed', { entity: t('sales:customers.workspace', 'Workspace') }));
    }
  };

  // Addresses section (List+Meta style)
  const addressesSection = (
    <Card
      style={{ marginBottom: 24 }}
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Typography.Title level={5} style={{ margin: 0 }}>{t('common:addresses')}</Typography.Title>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAddress}>
              {t('common:addAddress')}
            </Button>
          </Col>
        </Row>
      }
    >
      <List
        dataSource={addresses}
        locale={{ emptyText: t('common:noData') }}
        renderItem={(address: Address) => (
          <List.Item
            actions={[
              <Button type="link" onClick={() => handleEditAddress(address)}>{t('common:edit')}</Button>,
              <Button type="link" danger onClick={() => handleDeleteAddress(address)}>{t('common:delete')}</Button>
            ]}
          >
            <List.Item.Meta
              title={t('sales:customers.addressType.' + address.type, address.type as string)}
              description={
                <Space direction="vertical" size={0}>
                  <Typography.Text type="secondary">{address.address}</Typography.Text>
                  <Typography.Text type="secondary">{address.city}</Typography.Text>
                  <Typography.Text type="secondary">{address.pincode}</Typography.Text>
                  <Typography.Text type="secondary">{address.country}</Typography.Text>
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );

  // Contacts section (List+Meta style)
  const contactsSection = (
    <Card
      style={{ marginBottom: 24 }}
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Typography.Title level={5} style={{ margin: 0 }}>{t('common:contacts')}</Typography.Title>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddContact}>
              {t('common:addContact')}
            </Button>
          </Col>
        </Row>
      }
    >
      <List
        dataSource={contacts}
        locale={{ emptyText: t('common:noData') }}
        renderItem={(contact: Contact) => (
          <List.Item
            actions={[
              <Button type="link" onClick={() => handleEditContact(contact)}>{t('common:edit')}</Button>,
              <Button type="link" danger onClick={() => handleDeleteContact(contact)}>{t('common:delete')}</Button>
            ]}
          >
            <List.Item.Meta
              title={contact.name}
              description={
                <Space direction="vertical" size={0}>
                  <Typography.Text type="secondary">{contact.position}</Typography.Text>
                  <Typography.Text type="secondary">{contact.email}</Typography.Text>
                  <Typography.Text type="secondary">{contact.phone}</Typography.Text>
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );

  // Workspace section
  const workspaceSection = (
    <Card style={{ marginBottom: 24 }}>
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label={t('sales:customers.fields.workspaceName')}>{workspace?.name || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('sales:customers.fields.workspaceSlug')}>{workspace?.slug || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('sales:customers.fields.language', 'Language')}>{workspace?.language || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('sales:customers.fields.currency', 'Currency')}>{workspace?.currency || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('sales:customers.fields.presidentName', 'President Name')}>{workspace?.presidentName || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('sales:customers.fields.presidentEmail', 'President Email')}>{workspace?.presidentEmail || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('sales:customers.fields.presidentPhone', 'President Phone')}>{workspace?.presidentPhone || '-'}</Descriptions.Item>
      </Descriptions>
      <Button style={{ marginTop: 16 }} onClick={handleEditWorkspace}>{workspace ? t('common:edit') : t('workspaces:add')}</Button>
      <Modal
        open={workspaceEditModalOpen}
        title={t('sales:customers.workspace', 'Workspace') + ' ' + (workspace ? t('common:edit') : t('workspaces:add'))}
        onOk={handleSaveWorkspace}
        onCancel={() => setWorkspaceEditModalOpen(false)}
        destroyOnHidden
      >
        <Form layout="vertical">
          <Form.Item label={t('sales:customers.fields.workspaceName')} required>
            <Input value={workspaceForm?.name} onChange={e => handleWorkspaceFormChange('name', e.target.value)} />
          </Form.Item>
          <Form.Item label={t('sales:customers.fields.workspaceSlug')} required>
            <Input value={workspaceForm?.slug} onChange={e => handleWorkspaceFormChange('slug', e.target.value)} />
          </Form.Item>
          <Form.Item label={t('sales:customers.fields.language', 'Language')} required>
            <Input value={workspaceForm?.language} onChange={e => handleWorkspaceFormChange('language', e.target.value)} />
          </Form.Item>
          <Form.Item label={t('sales:customers.fields.currency', 'Currency')} required>
            <Input value={workspaceForm?.currency} onChange={e => handleWorkspaceFormChange('currency', e.target.value)} />
          </Form.Item>
          <Form.Item label={t('sales:customers.fields.presidentName', 'President Name')} required={false}>
            <Input value={workspaceForm?.presidentName} onChange={e => handleWorkspaceFormChange('presidentName', e.target.value)} />
          </Form.Item>
          <Form.Item label={t('sales:customers.fields.presidentEmail', 'President Email')} required={false}>
            <Input value={workspaceForm?.presidentEmail} onChange={e => handleWorkspaceFormChange('presidentEmail', e.target.value)} />
          </Form.Item>
          <Form.Item label={t('sales:customers.fields.presidentPhone', 'President Phone')} required={false}>
            <Input value={workspaceForm?.presidentPhone} onChange={e => handleWorkspaceFormChange('presidentPhone', e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );

  // Tabs items array for new AntD Tabs API
  const tabItems = [
    {
      key: 'details',
      label: t('sales:customers.detailsSection', 'Customer Details'),
      children: (
        <Card style={{ marginBottom: 24 }}>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={t('sales:customers.fields.propertyName')}>{customer.property_name}</Descriptions.Item>
            <Descriptions.Item label={t('sales:customers.fields.status')}>{customer.status}</Descriptions.Item>
            <Descriptions.Item label={t('sales:customers.fields.pricePlan')}>{customer.price_plan_name}</Descriptions.Item>
            <Descriptions.Item label={t('sales:customers.fields.billingCycle')}>{customer.billing_cycle}</Descriptions.Item>
            <Descriptions.Item label={t('sales:customers.fields.billingStartDate')}>{dayjs(customer.billing_start_date).format('YYYY-MM-DD')}</Descriptions.Item>
            <Descriptions.Item label={t('sales:customers.fields.paymentMethod')}>{customer.payment_method}</Descriptions.Item>
            <Descriptions.Item label={t('common:createdAt')}>{dayjs(customer.created_at).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
            <Descriptions.Item label={t('common:updatedAt')}>{dayjs(customer.updated_at).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
            <Descriptions.Item label={t('common:description')}>{customer.description}</Descriptions.Item>
            <Descriptions.Item label={t('common:notes')}>
              {customer.notes ? formatTextWithLineBreaks(customer.notes) : t('common:none')}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'addresses',
      label: t('common:addresses', 'Addresses'),
      children: addressesSection,
    },
    {
      key: 'contacts',
      label: t('common:contacts', 'Contacts'),
      children: contactsSection,
    },
    {
      key: 'workspace',
      label: t('sales:customers.workspace', 'Workspace'),
      children: workspaceSection,
    },
    {
      key: 'invoices',
      label: t('sales:invoices.title', 'Invoices'),
      children: (
        <Card style={{ marginBottom: 24 }}>
          {/* TODO: Show invoices table */}
        </Card>
      ),
    },
  ];

  // Add delete handler
  const handleDeleteCustomer = () => {
    setOpenDeleteDialog(true);
  };
  const handleConfirmDelete = async () => {
    if (!customer) return;
    try {
      await deleteCustomer(customer.id);
      message.success(t('sales:success.deleteSuccess', { entity: t('customer') }));
      setOpenDeleteDialog(false);
      navigate('/sales/customers');
    } catch (e) {
      message.error(t('sales:errors.deleteFailed', { entity: t('customer') }));
    }
  };
  const handleCancelDelete = () => setOpenDeleteDialog(false);

  return (
    <div style={{ width: '100%' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space align="center">
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => window.history.back()} />
            <Title level={3} style={{ margin: 0 }}>{customer.property_name}</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>{t('common:edit')}</Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDeleteCustomer}>{t('common:delete')}</Button>
          </Space>
        </Col>
      </Row>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
      {editModalOpen && (
        <CustomerModal
          open={editModalOpen}
          customer={editingCustomer}
          onSuccess={handleEditModalSuccess}
          onCancel={() => setEditModalOpen(false)}
        />
      )}
      {openContactModal && (
        <ContactEditModal
          open={openContactModal}
          contact={editingContact || undefined}
          onSave={handleSaveContact}
          onCancel={() => setOpenContactModal(false)}
          contactTypeOptions={[
            { value: 'Primary', label: 'Primary' },
            { value: 'Secondary', label: 'Secondary' },
            { value: 'Decision Maker', label: 'Decision Maker' },
          ]}
          t={t}
        />
      )}
      {openAddressModal && (
        <AddressEditModal
          open={openAddressModal}
          address={editingAddress || undefined}
          onSave={handleSaveAddress}
          onCancel={() => { setOpenAddressModal(false); setEditingAddress(null); }}
          t={t}
        />
      )}
      {openDeleteAddressModal && (
        <ConfirmDeleteModal
          open={openDeleteAddressModal}
          onOk={confirmDeleteAddress}
          onCancel={() => { setOpenDeleteAddressModal(false); setAddressToDelete(null); }}
          title={t('common:confirmDeleteTitle')}
          description={t('common:confirmDelete')}
          okText={t('common:delete')}
          cancelText={t('common:cancel')}
        />
      )}
      {openDeleteContactModal && (
        <ConfirmDeleteModal
          open={openDeleteContactModal}
          onOk={confirmDeleteContact}
          onCancel={() => { setOpenDeleteContactModal(false); setContactToDelete(null); }}
          title={t('common:confirmDeleteTitle')}
          description={t('common:confirmDelete')}
          okText={t('common:delete')}
          cancelText={t('common:cancel')}
        />
      )}
      <Modal
        title={t('common:confirmDeleteTitle')}
        open={openDeleteDialog}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        okText={t('common:delete')}
        cancelText={t('common:cancel')}
        okButtonProps={{ danger: true }}
        width={400}
        destroyOnHidden
      >
        <Typography.Paragraph>{t('common:confirmDelete')}</Typography.Paragraph>
      </Modal>
      {/* TODO: ContactEditModal, PricePlanModal, WorkspaceModal, etc. */}
    </div>
  );
};

export default CustomerDetailsPage; 